/**
 * MobileNav - Bottom navigation bar for mobile devices (Owner Portal)
 * Touch-friendly navigation with key actions easily accessible
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import {
  Home,
  FileText,
  CreditCard,
  AlertTriangle,
  User,
  Menu,
  X,
  MessageSquare,
  Calendar,
  HelpCircle,
  LogOut,
  ClipboardCheck,
  Map,
  Megaphone,
  Building2
} from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";

const MobileNav = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");
  
  // Primary nav items (shown in bottom bar)
  const primaryNav = [
    { href: "/owner/dashboard", icon: Home, label: "Home" },
    { href: "/owner/payments", icon: CreditCard, label: "Pay" },
    { href: "/owner/violations", icon: AlertTriangle, label: "Notices" },
    { href: "/owner/documents", icon: FileText, label: "Docs" },
  ];
  
  // Secondary nav items (shown in menu sheet)
  const secondaryNav = [
    { href: "/owner/profile", icon: User, label: "My Profile" },
    { href: "/owner/drc", icon: ClipboardCheck, label: "DRC Submissions" },
    { href: "/owner/forum", icon: MessageSquare, label: "Community Forum" },
    { href: "/owner/events", icon: Calendar, label: "Events & Reservations" },
    { href: "/owner/announcements", icon: Megaphone, label: "Announcements" },
    { href: "/owner/gis", icon: Map, label: "Property Map" },
    { href: "/owner/knowledge-base", icon: HelpCircle, label: "Help & FAQs" },
  ];
  
  return (
    <>
      {/* Bottom Navigation Bar - Fixed at bottom on mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t shadow-lg md:hidden" data-testid="mobile-nav">
        <div className="flex items-center justify-around h-16 px-2 safe-area-inset-bottom">
          {primaryNav.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg transition-all duration-200 ${
                isActive(item.href)
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className={`w-5 h-5 ${isActive(item.href) ? "text-primary" : ""}`} />
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </Link>
          ))}
          
          {/* Menu Button */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-200"
                data-testid="mobile-nav-menu"
              >
                <Menu className="w-5 h-5" />
                <span className="text-[10px] mt-1 font-medium">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
              <SheetHeader className="pb-4 border-b">
                <SheetTitle className="text-left font-serif">Menu</SheetTitle>
              </SheetHeader>
              
              <div className="py-4 space-y-1 overflow-y-auto max-h-[calc(85vh-120px)]">
                {/* User Info */}
                <div className="flex items-center gap-3 p-3 mb-4 bg-muted/50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user?.name || "Owner"}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
                
                {/* Secondary Navigation */}
                {secondaryNav.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${
                      isActive(item.href)
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
                
                {/* Divider */}
                <div className="my-4 border-t" />
                
                {/* Logout */}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-3 p-3 rounded-xl w-full text-destructive hover:bg-destructive/10 transition-all duration-200"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      
      {/* Spacer for bottom nav on mobile */}
      <div className="h-16 md:hidden" />
    </>
  );
};

export default MobileNav;
